/**
 * Client-side audience segregation for analytics KPIs.
 *
 * Three filtered buckets (+ all):
 *   agents — partner-agent activity (@serhant.com for Serhant tenants), plus
 *            linked guestSessionId when the agent was identified in-session
 *   leads  — everyone else (external buyers, clients, guest visitors)
 *   all    — no filter
 *
 * Partner-agent identity is determined by email domain (from tenant config)
 * and the loaded partner user directory (covers Mongo _id on sign-up events).
 *
 * Recent Activity and CSV export stay on the raw feed.
 *
 * @magicfurnish.com rows can be stripped entirely via filterOutInternal()
 * (controlled by a separate on/off toggle in the UI).
 */

import {
  getPartnerAgentEmailDomain,
  getConfiguredPartnerAgentEmailDomains,
} from '../../utils/tenantConfig';
import { inferActorRole } from '../../utils/eventAttribution';

export const AUDIENCE_VALUES = ['agents', 'leads', 'all'];

/** Map legacy stored value `uploaders` → `agents`. */
export const normalizeAudience = (audience) => {
  if (audience === 'uploaders') return 'agents';
  return audience;
};

const extractRowEmails = (row) => {
  const emails = [];
  const u = row?.meta?.user;
  if (looksLikeEmail(u)) emails.push(u.trim());
  if (looksLikeEmail(row?.meta?.email)) emails.push(row.meta.email.trim());
  const detailEmail =
    (typeof row?.detail?.email === 'string' && row.detail.email) ||
    (typeof row?.detail?.userEmail === 'string' && row.detail.userEmail) ||
    null;
  if (detailEmail) emails.push(detailEmail.trim());
  return emails;
};

const looksLikeEmail = (s) =>
  typeof s === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

const isGenericMetaUser = (u) => !u || u === 'guest' || u === 'anonymous';

/** True when the email belongs to Magic Furnish platform staff. */
export const isMagicFurnishStaffEmail = (email) => {
  if (!looksLikeEmail(email)) return false;
  return email.trim().toLowerCase().endsWith('@magicfurnish.com');
};

/** True when the row is attributed to a non-@magicfurnish.com email address. */
export const rowHasNonMagicFurnishEmail = (row) => {
  const u = row?.meta?.user;
  if (looksLikeEmail(u) && !isMagicFurnishStaffEmail(u)) return true;
  if (looksLikeEmail(row?.meta?.email) && !isMagicFurnishStaffEmail(row.meta.email)) {
    return true;
  }
  return extractRowEmails(row).some((email) => !isMagicFurnishStaffEmail(email));
};

/** True when the email belongs to a partner agent (e.g. @serhant.com). */
export const isPartnerAgentEmail = (email, agentEmailDomain) => {
  if (!looksLikeEmail(email) || !agentEmailDomain) return false;
  return email.trim().toLowerCase().endsWith(String(agentEmailDomain).toLowerCase());
};

const emailMatchesAgentDomain = (email, primaryDomain) => {
  if (!looksLikeEmail(email)) return false;
  if (primaryDomain && isPartnerAgentEmail(email, primaryDomain)) return true;
  if (!primaryDomain) {
    return getConfiguredPartnerAgentEmailDomains().some((d) =>
      isPartnerAgentEmail(email, d)
    );
  }
  return false;
};

const addAgentIdentity = (set, identity) => {
  if (!identity || typeof identity !== 'string') return;
  const s = identity.trim();
  if (!s) return;
  set.add(s);
  if (looksLikeEmail(s)) set.add(s.toLowerCase());
};

const addInternalIdentity = addAgentIdentity;

/** True when stamped attribution or inferred role marks MagicFurnish staff. */
export const rowIsMagicFurnishStaffActivity = (row) => {
  if (row?.detail?.audience === 'internal') return true;
  const role = row?.detail?.actorRole || inferActorRole(row?.meta, row?.detail);
  return role === 'mf_staff';
};

/**
 * Build partner-agent user + session sets for audience classification.
 *
 * @returns {{
 *   partnerAgentUsers: Set<string>,
 *   partnerAgentSessions: Set<string>,
 *   agentEmailDomain: string|null,
 * }}
 */
export const buildPartnerAgentSets = (feedRows, opts = {}) => {
  const partnerAgentUsers = new Set();
  const partnerAgentSessions = new Set();
  const rows = Array.isArray(feedRows) ? feedRows : [];

  const agentEmailDomain =
    opts.agentEmailDomain ??
    getPartnerAgentEmailDomain(opts.partnerHostname ?? '') ??
    null;

  const emailIsAgent = (email) => emailMatchesAgentDomain(email, agentEmailDomain);

  // Directory holds every partner account (agents + buyer sign-ups). Only
  // index @serhant.com rows so external leads are not misclassified as agents.
  const partnerUsers = Array.isArray(opts?.partnerAdminUsers)
    ? opts.partnerAdminUsers
    : [];
  partnerUsers.forEach((user) => {
    const email = user?.email ? String(user.email) : '';
    if (!emailIsAgent(email)) return;
    addAgentIdentity(partnerAgentUsers, email);
    if (user?._id) addAgentIdentity(partnerAgentUsers, String(user._id));
  });

  rows.forEach((row) => {
    extractRowEmails(row).forEach((email) => {
      if (emailIsAgent(email)) addAgentIdentity(partnerAgentUsers, email);
    });
    const u = row?.meta?.user;
    if (looksLikeEmail(u) && emailIsAgent(u)) {
      addAgentIdentity(partnerAgentUsers, u);
    }
  });

  rows.forEach((row) => {
    const u = row?.meta?.user;
    const gs = row?.meta?.guestSessionId;
    if (!gs || typeof gs !== 'string') return;

    const isAgentRow =
      (!isGenericMetaUser(u) && partnerAgentUsers.has(String(u))) ||
      extractRowEmails(row).some(emailIsAgent);

    if (isAgentRow) partnerAgentSessions.add(gs);
  });

  return { partnerAgentUsers, partnerAgentSessions, agentEmailDomain };
};

/**
 * @returns {{
 *   partnerHostname: string,
 *   agentEmailDomain: string|null,
 *   partnerAgentUsers: Set<string>,
 *   partnerAgentSessions: Set<string>,
 * }}
 */
export const buildAudienceContext = (feedRows, partnerHostname, opts = {}) => {
  const { partnerAgentUsers, partnerAgentSessions, agentEmailDomain } =
    buildPartnerAgentSets(feedRows, { ...opts, partnerHostname });
  return {
    partnerHostname,
    agentEmailDomain,
    partnerAgentUsers,
    partnerAgentSessions,
  };
};

const rowIsPartnerAgent = (row, ctx) => {
  const u = row?.meta?.user;
  const gs = row?.meta?.guestSessionId;

  const emails = extractRowEmails(row);
  if (emails.some((email) => emailMatchesAgentDomain(email, ctx.agentEmailDomain))) {
    return true;
  }
  if (looksLikeEmail(u) && emailMatchesAgentDomain(u, ctx.agentEmailDomain)) {
    return true;
  }

  if (!isGenericMetaUser(u) && ctx.partnerAgentUsers.has(String(u))) {
    return true;
  }
  if (gs && ctx.partnerAgentSessions.has(String(gs))) {
    return true;
  }

  return false;
};

/** Classify a row as 'agents' or 'leads'. */
export const classifyRowAudience = (row, ctx) =>
  rowIsPartnerAgent(row, ctx) ? 'agents' : 'leads';

/** Filter feed rows to agents | leads. Returns input verbatim for 'all'. */
export const filterByAudience = (feedRows, audience, partnerHostname, opts = {}) => {
  if (!Array.isArray(feedRows)) return feedRows;
  const normalized = normalizeAudience(audience);
  if (normalized === 'all') return feedRows;
  if (!AUDIENCE_VALUES.includes(normalized)) return feedRows;

  const ctx = buildAudienceContext(feedRows, partnerHostname, opts);

  if (normalized === 'agents') {
    return feedRows.filter((row) => classifyRowAudience(row, ctx) === 'agents');
  }
  if (normalized === 'leads') {
    return feedRows.filter((row) => classifyRowAudience(row, ctx) === 'leads');
  }
  return feedRows;
};

/**
 * Build user + session sets for @magicfurnish.com staff.
 *
 * @returns {{
 *   internalUsers: Set<string>,
 *   internalSessions: Set<string>,
 *   sessionsWithExternalEmail: Set<string>,
 * }}
 */
export const buildInternalExclusion = (feedRows, opts = {}) => {
  const internalUsers = new Set();
  const internalSessions = new Set();
  const sessionsWithExternalEmail = new Set();
  const rows = Array.isArray(feedRows) ? feedRows : [];

  const partnerUsers = Array.isArray(opts.partnerAdminUsers) ? opts.partnerAdminUsers : [];
  partnerUsers.forEach((user) => {
    const email = user?.email ? String(user.email) : '';
    if (!isMagicFurnishStaffEmail(email)) return;
    addInternalIdentity(internalUsers, email);
    if (user?._id) addInternalIdentity(internalUsers, String(user._id));
  });

  rows.forEach((row) => {
    extractRowEmails(row).forEach((email) => {
      if (!isMagicFurnishStaffEmail(email)) return;
      addInternalIdentity(internalUsers, email);
      const u = row?.meta?.user;
      if (!isGenericMetaUser(u)) addInternalIdentity(internalUsers, String(u));
    });
    const u = row?.meta?.user;
    if (looksLikeEmail(u) && isMagicFurnishStaffEmail(u)) {
      addInternalIdentity(internalUsers, String(u).trim());
    }
    if (rowIsMagicFurnishStaffActivity(row) && !isGenericMetaUser(u)) {
      addInternalIdentity(internalUsers, String(u));
    }
  });

  rows.forEach((row) => {
    const u = row?.meta?.user;
    const gs = row?.meta?.guestSessionId;
    if (
      gs &&
      typeof gs === 'string' &&
      !isGenericMetaUser(u) &&
      internalUsers.has(String(u))
    ) {
      internalSessions.add(gs);
    }
  });

  const rowsBySession = new Map();
  rows.forEach((row) => {
    const gs = row?.meta?.guestSessionId;
    if (!gs || typeof gs !== 'string') return;
    if (!rowsBySession.has(gs)) rowsBySession.set(gs, []);
    rowsBySession.get(gs).push(row);
  });
  rowsBySession.forEach((sessionList, gs) => {
    if (sessionList.some((r) => rowHasNonMagicFurnishEmail(r))) {
      sessionsWithExternalEmail.add(gs);
    }
  });

  return { internalUsers, internalSessions, sessionsWithExternalEmail };
};

/** True when the row belongs to @magicfurnish.com staff (user or linked session). */
export const isInternalRow = (row, exclusion) => {
  if (!exclusion) return false;
  if (rowHasNonMagicFurnishEmail(row)) return false;
  if (rowIsMagicFurnishStaffActivity(row)) return true;

  const u = row?.meta?.user;
  const gs = row?.meta?.guestSessionId;

  if (!isGenericMetaUser(u) && exclusion.internalUsers.has(String(u))) return true;
  if (looksLikeEmail(u) && isMagicFurnishStaffEmail(u)) return true;
  if (gs && exclusion.internalSessions.has(String(gs))) {
    if (exclusion.sessionsWithExternalEmail?.has(String(gs))) return false;
    return true;
  }

  return extractRowEmails(row).some((email) => isMagicFurnishStaffEmail(email));
};

/** Remove all @magicfurnish.com activity (and linked guest sessions). */
export const filterOutInternal = (feedRows, exclusion) => {
  if (!Array.isArray(feedRows)) return feedRows;
  const ex = exclusion || {
    internalUsers: new Set(),
    internalSessions: new Set(),
    sessionsWithExternalEmail: new Set(),
  };
  return feedRows.filter((row) => !isInternalRow(row, ex));
};

/** Dedupe feed rows by stable id for merged classification context. */
export const mergeFeedRows = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((row) => {
    if (!row) return;
    const id =
      row._id ||
      `${row.timestamp}-${row.meta?.event}-${row.meta?.user}-${row.meta?.guestSessionId}`;
    byId.set(id, row);
  });
  return [...byId.values()];
};

/** True when any audience or internal-exclusion filter is active. */
export const isAudienceFilterActive = (audience, excludeInternal) =>
  normalizeAudience(audience) !== 'all' || Boolean(excludeInternal);

/** Human-readable label for drill-down filter notices. */
export const audienceFilterLabel = (audience, excludeInternal) => {
  const normalized = normalizeAudience(audience);
  const parts = [];
  if (normalized === 'leads') parts.push('Lead Activity');
  else if (normalized === 'agents') parts.push('Agent Activity');
  else if (normalized === 'all') parts.push('All Activity');
  if (excludeInternal) parts.push('@magicfurnish.com excluded');
  return parts.join(' · ');
};

/**
 * Apply audience + internal exclusion to `rows`. Uses `contextRows` (defaults
 * to `rows`) to build agent/internal sets so event-only drill-down slices
 * (e.g. sign-ups fetched from /timeseries) classify correctly against the
 * full loaded feed.
 */
export const applyAudienceFilters = (
  rows,
  {
    audience = 'all',
    excludeInternal = false,
    partnerHostname = '',
    partnerAdminUsers = [],
    contextRows = null,
  } = {}
) => {
  const toFilter = Array.isArray(rows) ? rows : [];
  const normalized = normalizeAudience(audience);
  if (!isAudienceFilterActive(normalized, excludeInternal)) return toFilter;

  const ctxSource =
    Array.isArray(contextRows) && contextRows.length > 0 ? contextRows : toFilter;

  let result = toFilter;
  if (normalized !== 'all' && AUDIENCE_VALUES.includes(normalized)) {
    const ctx = buildAudienceContext(ctxSource, partnerHostname, { partnerAdminUsers });
    if (normalized === 'agents') {
      result = result.filter((row) => classifyRowAudience(row, ctx) === 'agents');
    } else if (normalized === 'leads') {
      result = result.filter((row) => classifyRowAudience(row, ctx) === 'leads');
    }
  }
  if (excludeInternal) {
    const exclusion = buildInternalExclusion(mergeFeedRows(ctxSource, toFilter), {
      partnerAdminUsers,
    });
    result = filterOutInternal(result, exclusion);
  }
  return result;
};
